import type { Optional } from 'utility-types';
import type { DateType, User } from 'api/models';
import type {
  ProjectPhasePreview,
  ProjectPreview,
  TaskPreview,
} from 'api/notifications';
import {
  ApiTimesheetStatus,
  TimesheetCalendarSourceType,
  TimesheetCategory,
  TimesheetEntryType,
  TimesheetSourceType,
  TimesheetUISourceType,
} from 'api/timesheets';
import type { Option } from 'components/atoms/select/types';

export enum WeekPreset {
  ThisWeek = 'this-week',
  PreviousWeek = 'previous-week',
}

export enum FilterViewType {
  ProjectsIOwn = 'projects_i_own',
  AllTimesheets = 'all_timesheets',
  Submitted = 'submitted',
  NotSubmitted = 'not_submitted',
  Approved = 'approved',
  Rejected = 'rejected',
  MyTeam = 'my_team',
}

export interface TimeEntryTaskPreview extends TaskPreview {
  defaultBillable?: boolean;
  defaultCategoryId?: number;
  effort?: number;
  deleted?: boolean;
}

export const FilterViewOptions = [
  FilterViewType.MyTeam,
  FilterViewType.AllTimesheets,
  FilterViewType.ProjectsIOwn,
  FilterViewType.Submitted,
  FilterViewType.NotSubmitted,
  FilterViewType.Approved,
  FilterViewType.Rejected,
];

export interface FilterItem extends Option {
  count: number;
}

export enum TimeEntryState {
  NotCreated = 'NotCreated',
  Creating = 'Creating',
  PartiallyFilled = 'PartiallyFilled',
  Loaded = 'Loaded',
}
export interface TimesheetTableTimeEntry {
  timeEntryId: number;
  notes?: string;
  category?: TimesheetCategory;
  billable: boolean;
  minutes: number;
  inState: TimeEntryState;
  disable?: boolean;
  roleId?: number;
  teamId?: number;
}

export interface TimesheetTask extends TimeEntryTaskPreview {
  entryType: TimesheetEntryType.TaskEntry;
  sourceType: TimesheetSourceType;
  sourceId: number;
  approvalStatus: TimesheetStatus;
}

interface BaseTimesheetTask {
  activityName: string;
  project?: ProjectPreview;
  defaultBillable?: boolean;
  deleted?: boolean;
  defaultCategoryId?: number;
}
export interface TimesheetAdhocTask extends BaseTimesheetTask {
  entryType: TimesheetEntryType.AdhocEntry;
  sourceType: TimesheetUISourceType;
  approvalStatus: TimesheetStatus;
}

export interface TimesheetCalendarTask extends BaseTimesheetTask {
  entryType: TimesheetEntryType.Integration;
  sourceType: TimesheetCalendarSourceType;
  sourceId: string;
  approvalStatus: TimesheetStatus;
}

export interface ProjectRow {
  project: ProjectPreview;
  defaultCategoryId?: number | undefined;
  entryType: TimesheetEntryType.ProjectEntry;
  sourceId: number;
  sourceType: TimesheetSourceType;
  billable?: boolean;
  approvalStatus: TimesheetStatus;
}

export interface PhaseTask {
  project: ProjectPreview;
  projectPhase: ProjectPhasePreview;
  entryType: TimesheetEntryType.PhaseEntry;
  defaultCategoryId?: number | undefined;
  billable?: boolean;
  status?: number;
  sourceId: number;
  sourceType: TimesheetSourceType;
  private?: boolean;
  startDate?: DateType;
  dueDate?: DateType;
  approvalStatus: TimesheetStatus;
}

export type TimesheetWeeklyRowTask =
  | TimesheetTask
  | TimesheetAdhocTask
  | TimesheetCalendarTask
  | ProjectRow
  | PhaseTask;

export type EntityTask = TimesheetTask | ProjectRow | PhaseTask;
export interface TimesheetWeeklyRow {
  task: TimesheetWeeklyRowTask;
  days: Record<string, TimesheetTableTimeEntry[]>;
}

export interface TimesheetWeekSummary {
  totalMinutesForDays: Record<string, number>;
  totalMinutesForWeek: number;
  totalBillableMinutesForWeek: number;
  totalNonBillableMinutesForWeek: number;
}

export interface TotalMinutes {
  totalBillableMinutes?: number;
  totalMinutes: number;
  totalNonBillableMinutes?: number;
}

export interface UserLevelSummary {
  user: User;
  userId: number;
  startDate: string;
  days: Record<string, TotalMinutes>;
  status: TimesheetStatus;
  updatedAt: number;
  lastReminderAt: number;
  projectId: number;
  projectName?: string;
  reminders: TimesheetReminder[];
}

export enum TimesheetStatus {
  NotSubmitted = 'not_submitted',
  Submitted = 'submitted',
  Approved = 'approved',
  Rejected = 'rejected',
}

export interface TimesheetReminder {
  createdBy: number;
  createdAt: number;
}

export interface TimesheetTimeEntry {
  timeEntryId: number;
  date: string;
  notes?: string;
  category?: TimesheetCategory;
  billable: boolean;
  minutes: number;
  userId: number;
  inState: TimeEntryState;
  status: ApiTimesheetStatus;
  disable?: boolean;
  roleId?: number;
  teamId?: number;
}
export interface TimesheetTaskEntry extends TimesheetTimeEntry {
  type: TimesheetEntryType.TaskEntry;
  task: TimeEntryTaskPreview;
  sourceId: number;
  sourceType: TimesheetUISourceType.Task;
  project: ProjectPreview;
  eventId?: string;
}

export interface TimesheetAdhocEntry extends TimesheetTimeEntry {
  type: TimesheetEntryType.AdhocEntry;
  activityName: string;
  project: ProjectPreview;
  sourceType: TimesheetUISourceType.Adhoc;
}

export interface TimesheetProjectEntry extends TimesheetTimeEntry {
  type: TimesheetEntryType.ProjectEntry;
  project: ProjectPreview;
  sourceId?: number;
  sourceType?: TimesheetUISourceType.Project;
}

export interface TimesheetPhaseEntry extends TimesheetTimeEntry {
  type: TimesheetEntryType.PhaseEntry;
  projectPhase: ProjectPhasePreview;
  project: ProjectPreview;
  sourceId: number;
  sourceType: TimesheetUISourceType.Phase;
  eventId?: string;
}

export interface TimesheetCalendarIntegrationEntry extends TimesheetTimeEntry {
  type: TimesheetEntryType.Integration;
  activityName: string;
  project: ProjectPreview;
  sourceId: string;
  sourceType: TimesheetCalendarSourceType;
}

export type TimesheetEntry =
  | TimesheetTaskEntry
  | TimesheetAdhocEntry
  | TimesheetProjectEntry
  | TimesheetPhaseEntry
  | Optional<TimesheetCalendarIntegrationEntry, 'project'>;

export type TimesheetAddFromCalendarEntry =
  | TimesheetCalendarIntegrationEntry
  | TimesheetTaskEntry
  | TimesheetPhaseEntry
  | TimesheetAdhocEntry;

interface TimesheetProject {
  id: number;
}
export interface Timesheet {
  entries: TimesheetEntry[];
  reminders: TimesheetReminder[];
  status: TimesheetStatus;
  userId: number;
  startDate: string;
  updatedAt: number;
  project: TimesheetProject;
  totalTime?: number;
}
interface BaseUserTimesheet {
  user: User;
  startDate: string;
  updatedAt: number;
  project: {
    id: number;
    name?: string;
  };
}

export interface ApprovedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.Approved;
}

export interface SubmittedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.Submitted;
}

export interface RejectedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.Rejected;
}
export interface NonSubmittedUserTimesheet extends BaseUserTimesheet {
  entries: TimesheetTableTimeEntry[];
  status: TimesheetStatus.NotSubmitted;
  reminders: TimesheetReminder[];
}

export interface PhaseTaskPreview {
  project: ProjectPreview;
  projectPhase: ProjectPhasePreview;
  billable?: boolean;
  category?: TimesheetCategory;
}

export interface ProjectTask {
  project: Option;
  billable?: boolean;
  category?: TimesheetCategory;
}

export type UserTimesheet =
  | NonSubmittedUserTimesheet
  | SubmittedUserTimesheet
  | RejectedUserTimesheet
  | ApprovedUserTimesheet;

export enum WorkType {
  GoogleCalendar = 'GOOGLE_CALENDAR',
  OutlookCalendar = 'OUTLOOK_CALENDAR',
  Task = 'TASK',
  Project = 'PROJECT',
  Phase = 'PHASE',
  Adhoc = 'ADHOC',
  Milestone = 'MILESTONE',
}

export enum NoteType {
  TimesheetReject = 'TIMESHEET_REJECT',
}

export enum NoteSubType {
  ProjectReject = 'PROJECT_REJECT',
  WorkReject = 'WORK_REJECT',
  OverallReject = 'OVERALL_REJECT',
}

export type RejectNoteType = {
  projectId: number;
  projectName: string;
  workType: WorkType;
  workId: number;
  workName: number;
  userId: number;
  noteId: number;
  noteText: string;
  type: NoteType;
  subType: NoteSubType;
  createdBy: number;
  createdAt: number;
};
